import { useState } from 'react';
import { Hash, Trash2, AlertTriangle } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Dropzone } from '@/components/ui/Dropzone';
import { Badge } from '@/components/ui/Badge';
import { formatBytes, readFileAsArrayBuffer } from '@/lib/utils';
import { cn } from '@/lib/cn';

interface Item {
  file: File;
  hash: string;
}

interface Group {
  hash: string;
  items: Item[];
  size: number;
}

async function sha256(buf: ArrayBuffer): Promise<string> {
  const out = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(out))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export function DuplicateFinder() {
  const [items, setItems] = useState<Item[]>([]);
  const [working, setWorking] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  async function handleFiles(newFiles: File[]) {
    setWorking(true);
    setProgress({ done: 0, total: newFiles.length });
    const out: Item[] = [];
    for (let i = 0; i < newFiles.length; i++) {
      const f = newFiles[i];
      const buf = await readFileAsArrayBuffer(f);
      const h = await sha256(buf);
      out.push({ file: f, hash: h });
      setProgress({ done: i + 1, total: newFiles.length });
    }
    setItems((prev) => [...prev, ...out]);
    setWorking(false);
  }

  function reset() {
    setItems([]);
  }

  // Group by hash
  const groups: Group[] = (() => {
    const map = new Map<string, Item[]>();
    for (const it of items) {
      const arr = map.get(it.hash) ?? [];
      arr.push(it);
      map.set(it.hash, arr);
    }
    return [...map.entries()]
      .map(([hash, arr]) => ({
        hash,
        items: arr,
        size: arr[0].file.size,
      }))
      .sort((a, b) => b.items.length - a.items.length);
  })();

  const dupeGroups = groups.filter((g) => g.items.length > 1);
  const wasted = dupeGroups.reduce((s, g) => s + g.size * (g.items.length - 1), 0);

  return (
    <div>
      <ToolHeader
        toolId="duplicate-finder"
        title="Duplicate file finder"
        description="Hash every file and group identical contents by SHA-256. See exactly what's wasting space."
        badges={
          items.length ? (
            <>
              <Badge>{items.length} files</Badge>
              {dupeGroups.length > 0 && (
                <Badge variant="warning">
                  {dupeGroups.length} duplicate group{dupeGroups.length === 1 ? '' : 's'}
                </Badge>
              )}
            </>
          ) : null
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <Card>
            <Dropzone
              multiple
              onFiles={handleFiles}
              hint="Drop any number of files — duplicates are grouped by content"
              icon={<Hash size={20} />}
            />
          </Card>
        </div>
        <Card className="space-y-3">
          <div className="space-y-1.5 text-sm">
            <Row label="Files scanned" value={items.length.toString()} />
            <Row label="Duplicate groups" value={dupeGroups.length.toString()} />
            <Row label="Wasted space" value={formatBytes(wasted)} accent />
          </div>
          {working && (
            <div>
              <div className="text-xs text-fg-muted mb-1">
                Hashing {progress.done}/{progress.total}
              </div>
              <div className="h-1 bg-bg-inset rounded-full overflow-hidden">
                <div
                  className="h-full bg-accent transition-all"
                  style={{ width: `${(progress.done / Math.max(1, progress.total)) * 100}%` }}
                />
              </div>
            </div>
          )}
          <Button size="sm" variant="ghost" className="w-full" onClick={reset} disabled={!items.length}>
            <Trash2 size={11} />
            Reset
          </Button>
        </Card>
      </div>

      {dupeGroups.length > 0 && (
        <div className="mt-4 space-y-3">
          {dupeGroups.map((g) => (
            <Card key={g.hash} className="!p-0">
              <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle size={14} className="text-warning" />
                  <span className="text-sm font-medium">
                    {g.items.length} identical files · {formatBytes(g.size)} each
                  </span>
                </div>
                <code className="text-2xs font-mono text-fg-subtle">
                  sha-256 {g.hash.slice(0, 12)}…
                </code>
              </div>
              <div className="divide-y divide-border">
                {g.items.map((it, i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-2 text-sm">
                    <span className="text-2xs font-mono text-fg-subtle w-6 text-right">
                      {i + 1}
                    </span>
                    <span className={cn('truncate', i === 0 ? 'font-medium' : 'text-fg-muted')}>
                      {it.file.name}
                    </span>
                    {i === 0 ? (
                      <Badge variant="success">keep</Badge>
                    ) : (
                      <Badge variant="warning">duplicate</Badge>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}

      {items.length > 0 && dupeGroups.length === 0 && !working && (
        <Card className="mt-4">
          <div className="py-4 text-center text-sm text-success">
            <Hash size={18} className="inline mr-2" />
            All {items.length} files are unique.
          </div>
        </Card>
      )}
    </div>
  );
}

function Row({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-fg-muted text-xs">{label}</span>
      <span className={cn('font-mono', accent && 'text-warning font-semibold')}>{value}</span>
    </div>
  );
}

export default DuplicateFinder;
